import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { toast } from "sonner";
import { JobForm } from "../../components/JobForm";
import { QuoteView } from "../../components/QuoteView";

const LOST_REASONS = [["price", "Price"], ["timing", "Timing"], ["went_elsewhere", "Went elsewhere"], ["no_response", "No response"], ["other", "Other"]];
const STATUS_CLS = { draft: "bg-muted", quoted: "bg-[hsl(43,74%,50%)] text-[hsl(240,10%,10%)]", approved: "bg-primary text-primary-foreground", lost: "bg-destructive text-destructive-foreground", scheduled: "bg-primary text-primary-foreground", in_progress: "bg-[hsl(196,72%,56%)] text-[hsl(240,10%,10%)]", complete: "bg-primary text-primary-foreground", invoiced: "bg-primary text-primary-foreground" };

export default function JobDetail() {
  const { id } = useParams();
  const [j, setJ] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [dates, setDates] = useState([]);
  const [day, setDay] = useState("");
  const [taxCfg, setTaxCfg] = useState(null);

  const [showQuote, setShowQuote] = useState(false);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJ(r.data)).catch((e) => toast.error(fmtErr(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the route id changes; load is stable
  useEffect(() => { load(); api.get("/settings").then((r) => setTaxCfg({ enabled: r.data.tax_enabled, rate: r.data.tax_rate, label: r.data.tax_label || "HST" })).catch(() => {}); }, [id]);

  if (!j) return <Layout title="Project job"><p className="text-muted-foreground">Loading…</p></Layout>;

  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(fmtErr(e)); } };

  const sendQuote = async () => {
    try {
      const r = await api.post(`/jobs/${id}/send-quote`);
      if (r.data.email_sent) toast.success(`Quote emailed to ${r.data.quote_sent_to || "the owner"}`);
      else toast.error("Quote saved but the email did NOT go out — re-send, or check the owner's notification settings");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Layout title={j.title}>
      <div className="space-y-6">
        <div className="card-flat p-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-heading text-xl font-bold" data-testid="job-title">{j.title}</p>
              <p className="text-sm text-muted-foreground">{j.property_name} · {j.owner_name}</p>
              {j.description && <p className="mt-2 text-sm">{j.description}</p>}
            </div>
            <span data-testid="job-status" className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_CLS[j.status] || "bg-muted"}`}>{j.status}</span>
          </div>
          {j.status === "lost" && <p className="mt-2 text-sm font-bold text-destructive" data-testid="job-lost-reason">Lost — {j.lost_reason}</p>}
        </div>

        <section data-testid="job-lines">
          <p className="label-caps mb-2">Line items</p>
          <div className="card-flat divide-y divide-border">
            {j.line_items.map((l, i) => (
              <div key={`${l.description}-${i}`} className="flex items-center justify-between gap-3 p-4" data-testid={`job-line-${i}`}>
                <div>
                  <p className="text-sm font-semibold">{l.description}</p>
                  <p className="text-xs text-muted-foreground capitalize">{l.kind} · qty {l.qty} × {fmtMoney(l.unit_price)}{l.cost ? ` · cost ${fmtMoney(l.cost)}` : ""}</p>
                </div>
                <span className="font-bold">{fmtMoney(l.amount)}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="card-flat space-y-1 p-4 text-sm" data-testid="job-totals">
          <p className="flex justify-between"><span>Subtotal</span><span className="font-bold">{fmtMoney(j.subtotal)}</span></p>
          {j.tax > 0 && <p className="flex justify-between"><span>{j.tax_label || taxCfg?.label || "HST"}</span><span className="font-bold">{fmtMoney(j.tax)}</span></p>}
          <p className="flex justify-between text-base"><span>Quoted total</span><span className="font-heading font-bold text-primary" data-testid="job-total">{fmtMoney(j.total)}</span></p>
          <p className="flex justify-between text-muted-foreground"><span>Your cost</span><span data-testid="job-cost">{fmtMoney(j.cost_total)}</span></p>
          <p className="flex justify-between font-bold text-primary"><span>Gross margin</span><span data-testid="job-margin">{fmtMoney(j.margin)}{j.margin_pct != null ? ` (${j.margin_pct}%)` : ""}</span></p>
          {j.deposit_percent > 0 && <p className="flex justify-between text-muted-foreground"><span>Deposit ({j.deposit_percent}%)</span><span data-testid="job-deposit">{fmtMoney(j.deposit_total ?? j.deposit_amount)}</span></p>}
        </div>

        {(j.deposit_invoice_id || j.final_invoice_id) && (
          <div className="space-y-2" data-testid="job-invoices">
            {j.deposit_invoice_id && <Link to={`/invoices/${j.deposit_invoice_id}`} data-testid="deposit-invoice-link" className="card-flat flex items-center justify-between p-4 font-semibold text-primary">Deposit invoice <span>→</span></Link>}
            {j.final_invoice_id && <Link to={`/invoices/${j.final_invoice_id}`} data-testid="final-invoice-link" className="card-flat flex items-center justify-between p-4 font-semibold text-primary">Final invoice (balance) <span>→</span></Link>}
          </div>
        )}

        {j.visit_ids?.length > 0 && (
          <section data-testid="job-visits">
            <p className="label-caps mb-2">Work days</p>
            <div className="space-y-2">
              {j.visit_ids.map((vid) => (
                <Link key={vid} to={`/visits/${vid}`} data-testid={`job-visit-${vid}`} className="card-flat block p-4 text-sm font-semibold text-primary">Visit {vid.slice(0, 8)}… →</Link>
              ))}
            </div>
          </section>
        )}

        {editing ? (
          <div className="card-flat p-4">
            <JobForm initial={j} properties={[]} taxCfg={taxCfg} testid="job-edit" onSaved={() => { setEditing(false); load(); }} />
          </div>
        ) : (
          <div className="space-y-3">
            {j.status === "draft" && (
              <button data-testid="job-edit-btn" onClick={() => setEditing(true)} className="h-12 w-full rounded-md bg-secondary font-bold text-secondary-foreground">Edit draft</button>
            )}
            {["draft", "quoted"].includes(j.status) && (
              <>
                <button data-testid="job-view-quote" onClick={() => setShowQuote(true)} className="h-12 w-full rounded-md border border-border font-bold">
                  {j.has_quote_document ? "View quote as sent" : "Preview quote"}
                </button>
                <button data-testid="job-send-quote" onClick={sendQuote} className="btn-primary-big !h-14 !text-base">
                  {j.status === "quoted" ? "Re-send quote email" : "Send quote to owner"}
                </button>
                <button data-testid="job-mark-lost-btn" onClick={() => setShowLost(!showLost)} className="h-12 w-full rounded-md border border-destructive font-bold text-destructive">Mark lost</button>
                {showLost && (
                  <div className="card-flat space-y-3 p-4" data-testid="lost-panel">
                    <p className="text-sm font-semibold">Why was it lost? (required — this feeds your win-rate numbers)</p>
                    <div className="flex flex-wrap gap-2">
                      {LOST_REASONS.map(([k, label]) => (
                        <button key={k} data-testid={`lost-reason-${k}`} onClick={() => setLostReason(k)}
                          className={`h-10 rounded-md border px-3 text-xs font-bold transition-colors ${lostReason === k ? "border-destructive bg-destructive text-destructive-foreground" : "border-border"}`}>{label}</button>
                      ))}
                    </div>
                    <button data-testid="lost-confirm" disabled={!lostReason} onClick={() => act(() => api.post(`/jobs/${id}/mark-lost`, { reason: lostReason }), "Marked lost")}
                      className="h-12 w-full rounded-md bg-destructive font-bold text-destructive-foreground disabled:opacity-40">Confirm lost</button>
                  </div>
                )}
              </>
            )}
            {["approved", "scheduled"].includes(j.status) && (
              <>
                <button data-testid="job-schedule-btn" onClick={() => setShowSchedule(!showSchedule)} className="btn-primary-big !h-14 !text-base">Schedule work days</button>
                {showSchedule && (
                  <div className="card-flat space-y-3 p-4" data-testid="schedule-panel">
                    <div className="flex gap-2">
                      <input data-testid="schedule-date" type="date" className="field-input flex-1" value={day} onChange={(e) => setDay(e.target.value)} />
                      <button data-testid="schedule-add-day" onClick={() => { if (day && !dates.includes(day)) setDates([...dates, day].sort()); setDay(""); }}
                        className="h-12 rounded-md bg-secondary px-4 text-sm font-bold text-secondary-foreground">Add day</button>
                    </div>
                    {dates.length > 0 && (
                      <div className="flex flex-wrap gap-2" data-testid="schedule-days">
                        {dates.map((d) => (
                          <span key={d} className="flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-bold">
                            {fmtDate(d)}
                            <button data-testid={`schedule-del-${d}`} onClick={() => setDates(dates.filter((x) => x !== d))} className="text-destructive">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <button data-testid="schedule-confirm" disabled={dates.length === 0} onClick={() => act(async () => { await api.post(`/jobs/${id}/schedule`, { dates }); setDates([]); setShowSchedule(false); }, "Work days scheduled — visits created")}
                      className="h-12 w-full rounded-md bg-primary font-bold text-primary-foreground disabled:opacity-40">Create {dates.length} visit{dates.length === 1 ? "" : "s"}</button>
                  </div>
                )}
              </>
            )}
            {["scheduled", "in_progress"].includes(j.status) && (
              <button data-testid="job-complete-btn" onClick={() => act(() => api.post(`/jobs/${id}/complete`), "Job complete — ready to invoice the balance")} className="btn-primary-big !h-14 !text-base">Mark job complete</button>
            )}
            {j.status === "complete" && !j.final_invoice_id && (
              <button data-testid="job-invoice-btn" onClick={() => act(() => api.post(`/jobs/${id}/invoice`), "Final invoice drafted")} className="btn-primary-big !h-14 !text-base">Create final invoice (balance)</button>
            )}
          </div>
        )}
        {["approved", "scheduled", "in_progress", "complete", "invoiced", "paid"].includes(j.status) && j.has_quote_document && (
          <button data-testid="job-view-quote" onClick={() => setShowQuote(true)} className="h-12 w-full rounded-md border border-border font-bold">View quote document</button>
        )}
        {showQuote && <QuoteView jobId={id} onClose={() => setShowQuote(false)} />}
      </div>
    </Layout>
  );
}
